import React, { useEffect, useState } from "react";
import { apiUtils } from "../../../utils/apiUtils";

const ROLE_OPTIONS = ["admin", "manager", "analyst", "viewer"];

export default function RepGuardTeamManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [permissions, setPermissions] = useState({
    canManageTeam: false,
    role: "viewer",
  });
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "viewer",
    phone: "",
  });
  const availableRoles = permissions.role === "manager"
    ? ROLE_OPTIONS.filter((role) => role !== "admin")
    : ROLE_OPTIONS;

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiUtils.get("/api/users/team-members");
      setMembers(response.data?.data?.members || []);
      setPermissions(response.data?.data?.permissions || {
        canManageTeam: false,
        role: "viewer",
      });
    } catch (loadError) {
      setError(loadError.message || "Failed to load team members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const handleCreateMember = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");
      const res = await apiUtils.post("/api/users/team-members", form);
      const body = res?.data || {};
      setForm({ name: "", email: "", role: "viewer", phone: "" });
      setSuccessMessage(
        body.message ||
          `Invitation sent to ${form.email}. They'll set their own password from the email.`
      );
      await loadMembers();
    } catch (createError) {
      setError(createError.message || "Failed to create team member.");
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (memberId, role) => {
    try {
      setError("");
      setSuccessMessage("");
      await apiUtils.put(`/api/users/team-members/${memberId}/role`, { role });
      setSuccessMessage("Team role updated.");
      await loadMembers();
    } catch (updateError) {
      setError(updateError.message || "Failed to update team role.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Team Management</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage RepCheck team members and assign reviewer-capable roles for RepGuard workflows.
        </p>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-200">
            {successMessage}
          </div>
        )}

        {!permissions.canManageTeam && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200">
            Your role is <span className="font-semibold">{permissions.role}</span>. Only admins and managers can manage team members.
          </div>
        )}

        {permissions.canManageTeam && (
          <form onSubmit={handleCreateMember} className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 items-end">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Full name</span>
              <input
                type="text"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Jane Doe"
                className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="jane@studio.com"
                className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Phone <span className="text-muted-foreground">(optional)</span></span>
              <input
                type="tel"
                value={form.phone}
                onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                placeholder="+1 555 000 0000"
                className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Role</span>
              <select
                value={form.role}
                onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value }))}
                className="w-full rounded-lg border border-input px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-neutral-400 sm:col-span-2 lg:col-span-4 lg:w-auto lg:justify-self-start"
            >
              {saving ? "Sending…" : "Send invitation"}
            </button>
          </form>
        )}
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground">Current team</h3>
        {loading ? (
          <div className="mt-4 text-muted-foreground">Loading team members...</div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {members.map((member) => {
                  // The account owner is the member with no teamOwner pointing
                  // elsewhere - i.e. the workspace itself. Their role is fixed:
                  // the server rejects changes to it from anyone they invited, so
                  // offering the dropdown would only produce a 403.
                  const isOwner =
                    !member.teamOwner || String(member.teamOwner) === String(member._id);
                  return (
                    <tr key={member._id}>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {member.name}
                        {isOwner && (
                          <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-200">
                            Account owner
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">{member.email}</td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {permissions.canManageTeam && !isOwner ? (
                          <select
                            value={member.role}
                            onChange={(event) => handleRoleChange(member._id, event.target.value)}
                            className="rounded-lg border border-input px-3 py-2"
                          >
                            {availableRoles.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        ) : (
                          member.role
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">{member.status || "active"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
